import React, { useState, useEffect, useCallback, useRef } from 'react';
import MPDashboardLayout from '../../components/mp/MPDashboardLayout';
import { mediaApi } from '../../lib/mpApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Upload, Search, Trash2, Image, Film, FileText, Copy, Loader2, Grid, List, Eye, Download,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (url) => url?.startsWith('/') ? `${BACKEND_URL}${url}` : url;
const isVideo = (url) => url && ['mp4','webm','ogg','mov','avi','mkv'].includes(url.split('?')[0].split('.').pop().toLowerCase());

const MPMediaPage = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [uploading, setUploading] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const fileInputRef = useRef(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await mediaApi.getAll();
      setMedia(Array.isArray(data) ? data : []);
    } catch { toast.error('Failed to load media'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = media.filter(m =>
    m.file_name?.toLowerCase().includes(search.toLowerCase()) ||
    m.caption?.toLowerCase().includes(search.toLowerCase())
  );

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        await mediaApi.upload(file);
      }
      toast.success(`${files.length} file(s) uploaded`);
      loadData();
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.file_name}"?`)) return;
    try { await mediaApi.delete(item.media_id); toast.success('Deleted'); loadData(); }
    catch { toast.error('Delete failed'); }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(resolveUrl(url));
    toast.success('URL copied');
  };

  return (
    <MPDashboardLayout title="Media Library">
      <div className="space-y-4" data-testid="mp-media-page">
        {/* Toolbar */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search media..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 rounded-sm" data-testid="mp-media-search" />
          </div>
          <div className="flex border border-border rounded-sm">
            <button onClick={() => setViewMode('grid')} className={`p-2 ${viewMode === 'grid' ? 'bg-accent' : ''}`}><Grid className="w-4 h-4" /></button>
            <button onClick={() => setViewMode('list')} className={`p-2 ${viewMode === 'list' ? 'bg-accent' : ''}`}><List className="w-4 h-4" /></button>
          </div>
          <input ref={fileInputRef} type="file" multiple accept="image/*,video/*" onChange={handleUpload} className="hidden" />
          <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="rounded-sm" data-testid="mp-media-upload-btn">
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Upload
          </Button>
        </div>

        {/* Media Grid/List */}
        {loading ? (
          <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground"><Image className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>No media files</p></div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {filtered.map(item => (
              <div key={item.media_id} className="group relative border border-border rounded-sm overflow-hidden aspect-square bg-muted" data-testid={`mp-media-item-${item.media_id}`}>
                {isVideo(item.file_url) ? (
                  <div className="w-full h-full flex items-center justify-center bg-muted">
                    <Film className="w-8 h-8 text-white/50" />
                  </div>
                ) : (
                  <img src={resolveUrl(item.file_url)} alt={item.file_name} className="w-full h-full object-cover" />
                )}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button size="sm" variant="secondary" onClick={() => setPreviewItem(item)} className="rounded-sm h-7"><Eye className="w-3 h-3" /></Button>
                  <Button size="sm" variant="secondary" onClick={() => copyUrl(item.file_url)} className="rounded-sm h-7"><Copy className="w-3 h-3" /></Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(item)} className="rounded-sm h-7"><Trash2 className="w-3 h-3" /></Button>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-2 py-1">
                  <p className="text-white text-[10px] truncate">{item.file_name}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(item => (
              <div key={item.media_id} className="flex items-center gap-4 p-3 border border-border rounded-sm" data-testid={`mp-media-item-${item.media_id}`}>
                <div className="w-12 h-12 rounded-sm overflow-hidden bg-muted flex-shrink-0">
                  {isVideo(item.file_url) ? (
                    <div className="w-full h-full flex items-center justify-center bg-muted"><Film className="w-5 h-5 text-white/50" /></div>
                  ) : (
                    <img src={resolveUrl(item.file_url)} alt="" className="w-full h-full object-cover" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{item.file_name}</p>
                  <p className="text-xs text-muted-foreground">{item.file_type} {item.caption && `· ${item.caption}`}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setPreviewItem(item)} className="rounded-sm"><Eye className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => copyUrl(item.file_url)} className="rounded-sm"><Copy className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(item)} className="rounded-sm text-destructive"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview Dialog */}
      <Dialog open={!!previewItem} onOpenChange={() => setPreviewItem(null)}>
        <DialogContent className="max-w-3xl rounded-sm">
          <DialogHeader><DialogTitle className="font-heading uppercase">{previewItem?.file_name}</DialogTitle></DialogHeader>
          {previewItem && (
            <div className="py-4">
              {isVideo(previewItem.file_url) ? (
                <video src={resolveUrl(previewItem.file_url)} className="w-full max-h-[60vh] rounded-sm" controls autoPlay />
              ) : (
                <img src={resolveUrl(previewItem.file_url)} alt="" className="w-full max-h-[60vh] object-contain rounded-sm" />
              )}
              {previewItem.caption && <p className="text-sm text-muted-foreground mt-3">{previewItem.caption}</p>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => copyUrl(previewItem?.file_url)} className="rounded-sm"><Copy className="w-4 h-4 mr-2" />Copy URL</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MPDashboardLayout>
  );
};

export default MPMediaPage;
